import {
  TreemapNodeKind,
  type ParsedTreemap,
  type TreemapTreeNode,
  type TreemapClassStyle,
} from './treemap-types';

const TREEMAP_HEADER_REGEX = /^treemap(-beta)?\b/i;
const TITLE_REGEX = /^title\s+(.+)$/i;
const CLASSDEF_REGEX = /^classDef\s+([A-Za-z0-9_-]+)\s+(.+)$/i;
// A `:::className` selector is always the trailing token of an item line.
const CLASS_SELECTOR_REGEX = /:::\s*([A-Za-z0-9_-]+)\s*$/;
const QUOTED_NAME_REGEX = /^"([^"]*)"/;
// A leaf value: `: <number>` at the end of the remainder after the name.
const VALUE_REGEX = /:\s*(-?\d+(?:\.\d+)?)\s*$/;
const STYLE_KEYS = new Set<keyof TreemapClassStyle>(['fill', 'stroke', 'color']);

interface ParsedItemLine {
  indent: number;
  name: string;
  value: number | null;
  className: string | null;
}

interface ParsedItem {
  name: string;
  value: number | null;
  className: string | null;
}

interface ContentWithClassSelector {
  content: string;
  className: string | null;
}

interface NameAndRemainder {
  name: string;
  remainder: string;
}

interface MutableNode {
  name: string;
  value: number | null;
  className: string | null;
  children: MutableNode[];
}

interface StackEntry {
  indent: number;
  node: MutableNode;
}

enum TreemapBodyLineKind {
  Skip = 'skip',
  Title = 'title',
  ClassDef = 'classDef',
  Item = 'item',
}

type TreemapBodyLine =
  | { kind: TreemapBodyLineKind.Skip }
  | { kind: TreemapBodyLineKind.Title; title: string | null }
  | { kind: TreemapBodyLineKind.ClassDef; className: string; style: TreemapClassStyle }
  | { kind: TreemapBodyLineKind.Item; itemLine: ParsedItemLine };

type TreemapBodyLineParser = (input: {
  line: string;
  trimmed: string;
}) => TreemapBodyLine | null;

interface CollectedTreemapBody {
  title: string | null;
  classDefs: Map<string, TreemapClassStyle>;
  itemLines: ParsedItemLine[];
}

function sanitizeMermaidLine(line: string): string {
  const commentIndex = line.indexOf('%%');
  const withoutComment = commentIndex >= 0 ? line.slice(0, commentIndex) : line;
  return withoutComment.replace(/\s+$/, '');
}

// Indent width: tabs count as 2, spaces as 1 (matches the mindmap parser).
function getIndentWidth(line: string): number {
  const leadingWhitespaceMatch = line.match(/^[\t ]*/);
  const leadingWhitespace = leadingWhitespaceMatch?.[0] ?? '';

  return [...leadingWhitespace].reduce((indent, char) => {
    return indent + (char === '\t' ? 2 : 1);
  }, 0);
}

// Parse a mermaid classDef body (`fill:#f00,stroke:#900,color:#fff`) into the
// safe presentational subset we support. Unknown keys are ignored.
function parseClassDefStyle(rawStyles: string): TreemapClassStyle {
  return rawStyles.split(',').reduce<TreemapClassStyle>((style, pair) => {
    const separatorIndex = pair.indexOf(':');
    if (separatorIndex < 0) {
      return style;
    }
    const key = pair.slice(0, separatorIndex).trim().toLowerCase();
    const value = pair.slice(separatorIndex + 1).trim();
    if (!value || !STYLE_KEYS.has(key as keyof TreemapClassStyle)) {
      return style;
    }
    return { ...style, [key]: value };
  }, {});
}

function stripClassSelector(rawContent: string): ContentWithClassSelector {
  const content = rawContent.trim();
  const classMatch = content.match(CLASS_SELECTOR_REGEX);
  if (!classMatch || classMatch.index == null) {
    return { content, className: null };
  }

  return {
    content: content.slice(0, classMatch.index).trimEnd(),
    className: classMatch[1] ?? null,
  };
}

function parseBareNameAndRemainder(content: string): NameAndRemainder {
  const colonIndex = content.indexOf(':');
  if (colonIndex < 0) {
    return { name: content.trim(), remainder: '' };
  }

  return {
    name: content.slice(0, colonIndex).trim(),
    remainder: content.slice(colonIndex).trim(),
  };
}

function parseNameAndRemainder(content: string): NameAndRemainder {
  const quotedMatch = content.match(QUOTED_NAME_REGEX);
  if (quotedMatch) {
    return {
      name: quotedMatch[1] ?? '',
      remainder: content.slice(quotedMatch[0].length).trim(),
    };
  }

  return parseBareNameAndRemainder(content);
}

function parseItemValue(remainder: string): number | null {
  const valueMatch = remainder.match(VALUE_REGEX);
  if (!valueMatch) {
    return null;
  }

  const parsed = Number(valueMatch[1]);
  return Number.isFinite(parsed) ? parsed : null;
}

// Parse one item line's content (already trimmed, indentation removed) into its
// name, optional leaf value, and optional class selector.
function parseItemLine(rawContent: string): ParsedItem | null {
  const { content, className } = stripClassSelector(rawContent);
  if (!content) {
    return null;
  }

  const { name, remainder } = parseNameAndRemainder(content);
  if (!name) {
    return null;
  }

  return { name, value: parseItemValue(remainder), className };
}

// Build the immutable tree, assigning ids/depth/kind and aggregating section
// values bottom-up (a section's value is the sum of its descendants — a
// section's own explicit value, if any, is overridden by the children sum).
function buildTreeNode(input: {
  node: MutableNode;
  path: number[];
  depth: number;
}): TreemapTreeNode {
  const { node, path, depth } = input;

  const children = node.children.map((child, childIndex) => {
    return buildTreeNode({
      node: child,
      path: [...path, childIndex],
      depth: depth + 1,
    });
  });

  const ownValue = node.value !== null && Number.isFinite(node.value) ? node.value : 0;
  const value =
    children.length > 0
      ? children.reduce((sum, child) => sum + child.value, 0)
      : ownValue;

  return {
    id: `treemap-${path.join('-')}`,
    label: node.name,
    value,
    kind: children.length > 0 ? TreemapNodeKind.Section : TreemapNodeKind.Leaf,
    className: node.className,
    depth,
    children,
  };
}

function parseEmptyBodyLine(input: { trimmed: string }): TreemapBodyLine | null {
  return input.trimmed ? null : { kind: TreemapBodyLineKind.Skip };
}

function parseTitleBodyLine(input: { trimmed: string }): TreemapBodyLine | null {
  const titleMatch = input.trimmed.match(TITLE_REGEX);
  if (!titleMatch) {
    return null;
  }

  return {
    kind: TreemapBodyLineKind.Title,
    title: (titleMatch[1] ?? '').trim() || null,
  };
}

function parseClassDefBodyLine(input: { trimmed: string }): TreemapBodyLine | null {
  const classDefMatch = input.trimmed.match(CLASSDEF_REGEX);
  if (!classDefMatch) {
    return null;
  }

  const className = classDefMatch[1];
  if (!className) {
    return null;
  }

  return {
    kind: TreemapBodyLineKind.ClassDef,
    className,
    style: parseClassDefStyle(classDefMatch[2] ?? ''),
  };
}

function parseItemBodyLine(input: { line: string; trimmed: string }): TreemapBodyLine | null {
  const item = parseItemLine(input.trimmed);
  if (!item) {
    return null;
  }

  return {
    kind: TreemapBodyLineKind.Item,
    itemLine: {
      indent: getIndentWidth(input.line),
      name: item.name,
      value: item.value,
      className: item.className,
    },
  };
}

const TREEMAP_BODY_LINE_PARSERS: TreemapBodyLineParser[] = [
  parseEmptyBodyLine,
  parseTitleBodyLine,
  parseClassDefBodyLine,
  parseItemBodyLine,
];

function isTreemapBodyLine(parsedLine: TreemapBodyLine | null): parsedLine is TreemapBodyLine {
  return parsedLine != null;
}

function parseTreemapBodyLine(line: string): TreemapBodyLine {
  const trimmed = line.trim();
  return (
    TREEMAP_BODY_LINE_PARSERS.map((parser) => parser({ line, trimmed })).find(isTreemapBodyLine) ?? {
      kind: TreemapBodyLineKind.Skip,
    }
  );
}

function collectTreemapBody(input: { lines: string[]; headerIndex: number }): CollectedTreemapBody {
  const classDefs = new Map<string, TreemapClassStyle>();
  const itemLines: ParsedItemLine[] = [];
  let title: string | null = null;

  input.lines.slice(input.headerIndex + 1).forEach((line) => {
    const parsedLine = parseTreemapBodyLine(line);
    if (parsedLine.kind === TreemapBodyLineKind.Title) {
      title = parsedLine.title;
      return;
    }
    if (parsedLine.kind === TreemapBodyLineKind.ClassDef) {
      classDefs.set(parsedLine.className, parsedLine.style);
      return;
    }
    if (parsedLine.kind === TreemapBodyLineKind.Item) {
      itemLines.push(parsedLine.itemLine);
    }
  });

  return { title, classDefs, itemLines };
}

export function parseMermaidTreemap(raw: string): ParsedTreemap | null {
  const lines = raw.split('\n').map((line) => sanitizeMermaidLine(line));
  const headerIndex = lines.findIndex((line) => TREEMAP_HEADER_REGEX.test(line.trim()));
  if (headerIndex === -1) {
    return null;
  }

  const { title, classDefs, itemLines } = collectTreemapBody({ lines, headerIndex });

  if (itemLines.length === 0) {
    return null;
  }

  // Synthetic root (indent -1) so multiple top-level entries are supported.
  const syntheticRoot: MutableNode = { name: '', value: null, className: null, children: [] };
  const baseIndent = itemLines[0].indent;
  const stack: StackEntry[] = [{ indent: -1, node: syntheticRoot }];

  itemLines.forEach((itemLine) => {
    const relativeIndent = Math.max(0, itemLine.indent - baseIndent);
    const nextNode: MutableNode = {
      name: itemLine.name,
      value: itemLine.value,
      className: itemLine.className,
      children: [],
    };

    while (stack.length > 1 && relativeIndent <= stack[stack.length - 1].indent) {
      stack.pop();
    }

    stack[stack.length - 1].node.children.push(nextNode);
    stack.push({ indent: relativeIndent, node: nextNode });
  });

  return {
    root: buildTreeNode({ node: syntheticRoot, path: [0], depth: 0 }),
    title,
    classDefs,
  };
}
